import { Events, on } from '../../../engine/core/events.js';
import { getStorageInfo } from '../../../engine/services/storage-service.js';
import { configureQuota, getQuotaInfo, quotaLevel, accountQuotaBlocked, isDocQuotaBlocked } from '../../../engine/services/quota-state.js';
import { createModal, showModal, closeModal, isModalOpen } from './modals.js';
import { t } from '../../../shared/i18n/index.js';
import { escapeHtml } from '../../../engine/core/validation.js';

// After saves and changes from others, the usage is asked again at most once
// a minute. Until 2026-10-15 it was every 1.5 s: an app open on a busy map
// asked about every 1.5 s, and at the busiest hour of 10,000 users (1,000
// online) that was more server work than all the editing (load runs with the
// app's requests: docs/internal/2026-10-12-cost-model.md). A change refused
// for quota, focus, coming back online and signing in ask at once.
const ACTIVITY_REFRESH_MS = 60000;
const SOON_MS = 1500;

/** One warning per threshold episode, retained across reloads for this account. */
export function initQuotaNotice({ getUser, getDoc, getCurrentDocId, onChanged, openVault }) {
  configureQuota({ getDoc, getUser });
  let queued = null, timer = null, timerAt = 0, lastRefresh = 0, displaying = null;
  const warnedMaps = new Set();
  let noticeUserId = null;
  const key = () => {
    noticeUserId = getUser()?.user_id || noticeUserId;
    return `orbital-quota-notices:${noticeUserId || ''}`;
  };
  let fallback = {};
  const read = () => { try { return JSON.parse(localStorage.getItem(key()) || '{}'); } catch { return fallback; } };
  const write = value => { fallback = value; try { localStorage.setItem(key(), JSON.stringify(value)); } catch { /* Memory still deduplicates. */ } };
  async function refresh() {
    if (!getUser()) return;
    lastRefresh = Date.now();
    try { await getStorageInfo({ force: true }); } catch { /* Keep known restrictions while offline. */ }
  }
  /** One refresh at a time; an earlier one already planned is kept. */
  function scheduleIn(delay) {
    const at = Date.now() + delay;
    if (timer && timerAt <= at) return;
    clearTimeout(timer);
    timerAt = at;
    timer = setTimeout(() => { timer = null; void refresh(); }, delay);
  }
  const refreshSoon = () => scheduleIn(SOON_MS);
  const refreshAfterActivity = () => scheduleIn(Math.max(SOON_MS, lastRefresh + ACTIVITY_REFRESH_MS - Date.now()));
  function showQueued() {
    if (!queued || displaying || isModalOpen() || !getUser()) return;
    const item = queued; queued = null;
    if (item.level === 'near' && quotaLevel(getQuotaInfo()?.totalBytes, getQuotaInfo()?.storageQuota) !== 'near') return;
    if (item.level === 'over' && !accountQuotaBlocked() && !isDocQuotaBlocked(getCurrentDocId())) return;
    displaying = item.level;
    const over = item.level === 'over';
    const modal = createModal({
      id: 'storage-quota-notice', title: t(over ? 'vault.quota.reachedTitle' : 'vault.quota.nearTitle'),
      className: 'storage-quota-modal',
      content: `<p>${escapeHtml(t(over ? (item.shared ? 'vault.quota.sharedOver' : 'vault.quota.over') : 'vault.quota.near'))}</p>
        ${over ? `<p>${escapeHtml(t('vault.quota.retained'))}</p>` : ''}`,
      buttons: [{ text: t('vault.quota.openVault'), action: 'vault', className: 'modal-btn-secondary' },
        { text: t('common.ok'), action: 'ok', className: 'modal-btn-primary' }],
    });
    const seen = read(); seen[over ? 'over' : 'near'] = true; write(seen);
    modal.querySelector('[data-action="vault"]').addEventListener('click', () => { closeModal(modal.id); openVault(); });
    modal.querySelector('[data-action="ok"]').addEventListener('click', () => closeModal(modal.id));
    document.body.appendChild(modal); showModal(modal.id);
  }
  function enqueue(level, shared = false) {
    if (displaying === 'over' || displaying === level) return;
    if (queued?.level === 'over') return;
    queued = { level, shared }; showQueued();
  }
  function update() {
    const info = getQuotaInfo();
    if (!info || info.quotaScope !== 'combined' || !getUser()) return;
    const level = quotaLevel(info.totalBytes, info.storageQuota), seen = read();
    const over = accountQuotaBlocked();
    if (!over) seen.over = false;
    if (['normal', 'warning'].includes(level)) seen.near = false;
    write(seen);
    if (over && !seen.over) enqueue('over');
    else if (level === 'near' && !seen.near) enqueue('near');
    const docId = getCurrentDocId();
    if (!isDocQuotaBlocked(docId)) warnedMaps.delete(docId);
    if (!over && isDocQuotaBlocked(docId) && !warnedMaps.has(docId)) {
      warnedMaps.add(docId);
      enqueue('over', !!getDoc(docId)?.role && getDoc(docId).role !== 'owner');
    }
    onChanged?.();
  }
  on(Events.QUOTA_CHANGED, update);
  on(Events.QUOTA_MAP_OPENED, ({ docId }) => {
    if (isDocQuotaBlocked(docId)) enqueue('over', !!getDoc(docId)?.role && getDoc(docId).role !== 'owner');
  });
  on(Events.QUOTA_REJECTED, refreshSoon);
  on(Events.MODAL_CLOSE, ({ modalId }) => {
    if (modalId === 'storage-quota-notice') { document.getElementById(modalId)?.remove(); displaying = null; }
    setTimeout(showQueued, 0);
  });
  on(Events.AUTH_LOGIN, () => { fallback = {}; warnedMaps.clear(); refreshSoon(); });
  on(Events.AUTH_LOGOUT, () => {
    queued = null; fallback = {}; warnedMaps.clear();
    if (noticeUserId) { try { localStorage.removeItem(`orbital-quota-notices:${noticeUserId}`); } catch { /* Unavailable storage. */ } }
    noticeUserId = null; closeModal('storage-quota-notice');
  });
  on(Events.SYNC_REMOTE_UPDATE, refreshAfterActivity);
  on(Events.SAVE_STATUS, ({ status } = {}) => { if (status === 'saved') refreshAfterActivity(); });
  window.addEventListener('focus', refreshSoon);
  window.addEventListener('online', refreshSoon);
  void refresh();
}
